import { Component, EventEmitter, inject, Input, OnInit, Output } from "@angular/core"
import { OrderListModule } from "primeng/orderlist"
import { ProgramService, SectionDTO } from "@services/program.service"
import { PickListModule } from "primeng/picklist"
import { SubSectionDTO, SubSectionService, SubVersionDTO } from "@services/sub-section.service"
import { InputTextModule } from "primeng/inputtext"
import { PaginatorModule } from "primeng/paginator"
import { ReactiveFormsModule } from "@angular/forms"
import { JsonPipe } from "@angular/common"
import { ButtonModule } from "primeng/button"
import { DialogService } from "primeng/dynamicdialog"
import { AdminSubsectionComponent } from "../admin-subsection/admin-subsection.component"
import { DialogModule } from "primeng/dialog"
import { SubSectionComponent } from "../../program/sub-section/sub-section.component"
import { SectionComponent } from "../../program/section/section.component"
import { SectionPubDTO } from "@services/program.published.service"
import { NotifService } from "@services/notif.service"
import { OverlayPanelModule } from "primeng/overlaypanel"

interface Item {
  id: string
  name: string
  name2: string
  name3: string // for filtering
  versions: Array<SubVersionDTO>
  versionId: string
  versionName: string
}

@Component({
  selector: "app-admin-section",
  standalone: true,
  imports: [
    OrderListModule,
    PickListModule,
    InputTextModule,
    PaginatorModule,
    ReactiveFormsModule,
    JsonPipe,
    ButtonModule,
    DialogModule,
    SectionComponent,
    SubSectionComponent,
    OverlayPanelModule,
  ],
  templateUrl: "./admin-section.component.html",
  styleUrl: "./admin-section.component.scss",
})
export class AdminSectionComponent implements OnInit {
  notifService = inject(NotifService)
  programService = inject(ProgramService)
  subSectionService = inject(SubSectionService)
  dialogService = inject(DialogService)
  source: Array<Item> = []
  target: Array<Item> = []
  expanded = false
  showPreviewPopup = false
  sectionPreview?: SectionPubDTO

  @Output() onSubsectionUpdated = new EventEmitter<string>()
  @Output() onMove = new EventEmitter<boolean>()
  @Output() onDelete = new EventEmitter()

  _subSections: Array<SubSectionDTO> = []
  @Input() set subSections(subs: Array<SubSectionDTO>) {
    this._subSections = subs
    this.source = subs.map((sub) => this.convertSubSectionToItem(sub))
    this.init()
  }

  @Input() _section: SectionDTO | undefined
  @Input() set section(section: SectionDTO) {
    this._section = section
    this.init()
  }

  async ngOnInit() {}

  getVersionName(v?: SubVersionDTO): string {
    if (!v) return ""
    return `${v.title || ""}   (${v.title_internal || ""})`
  }

  convertSubSectionToItem(subSection: SubSectionDTO, versionId2 = ""): Item {
    const versionId = versionId2 ? versionId2 : subSection.versions?.length > 0 ? subSection.versions[0]?.id || "" : ""
    const v = subSection.versions?.find((v) => v.id === versionId)
    return {
      id: subSection.id || "",
      name: subSection.title_internal,
      name2: `(${subSection.program_label} - ${subSection.category_label})`,
      name3: `${subSection.title_internal} (${subSection.program_label} - ${subSection.category_label})`,
      versions: subSection.versions,
      versionId,
      versionName: this.getVersionName(v),
    }
  }

  init() {
    this.target =
      this._section?.subsectionsRefs?.map((ref) => {
        const s = this.source.find((s) => s.id === ref.id)
        return {
          id: ref.id,
          name: s?.name || "",
          name2: s?.name2 || "",
          name3: s?.name3 || "",
          versionId: ref.versionId,
          versionName: this.getVersionName(s?.versions?.find((a) => a?.id === ref.versionId)),
          versions: this.source.find((s) => s.id === ref.id)?.versions || [],
        }
      }) || []

    if (this._section?.id && this._subSections?.length > 0) {
      this.source = this.source.filter((item) => !this._section?.subsectionsRefs?.map((x) => x.id)?.includes(item.id))
    }
  }

  openSubSectionDetails(id: string, versionId: string) {
    this.dialogService
      .open(AdminSubsectionComponent, {
        header: `Modification de page`,
        width: "98vw",
        data: {
          id,
          versionId,
        },
      })
      .onClose.subscribe(async (subSection) => {
        const index = this.source.findIndex((s) => s.id === subSection.id)
        let toUpdate = false
        if (index >= 0) {
          Object.assign(this.source[index], this.convertSubSectionToItem(subSection, this.source[index].versionId))
          toUpdate = true
        }

        const index2 = this.target.findIndex((s) => s.id === subSection.id)
        if (index2 >= 0) {
          Object.assign(this.target[index2], this.convertSubSectionToItem(subSection, this.target[index2].versionId))
          toUpdate = true
        }
        if (toUpdate) this.onSubsectionUpdated.emit(subSection.id)
      })
  }

  public toggleExpand(value: boolean) {
    this.expanded = value
  }

  async updatePreview(section: SectionDTO) {
    this.sectionPreview = await this.programService.convertSection(section)
  }

  updateSection() {
    // console.log("updateSection", this.target)
    if (this._section)
      this._section.subsectionsRefs = this.target.map((t) => ({
        id: t.id,
        versionId: t.versionId,
      }))
  }

  selectVersion(item: Item, version: SubVersionDTO) {
    item.versionId = version.id
    item.versionName = this.getVersionName(version)
    this.updateSection()
  }
}
